/**
 * Partymenu Calculator Web Component
 * Modern ES6+ implementation for party menu planning
 * Handles product selection, quantity management, price/weight calculations, and cart integration
 */

class PartymenuCalculator extends HTMLElement {
  constructor() {
    super();
    this.items = new Map();
    this.eaters = 10; // Default value
  }

  connectedCallback() {
    this.setupEventListeners();
    this.render();
    this.restoreFromLocalStorage();
  }

  setupEventListeners() {
    // Add to menu buttons
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-partymenu-add]');
      if (addBtn) {
        e.preventDefault();
        this.handleAddProduct(addBtn);
      }

      // Remove item buttons
      const removeBtn = e.target.closest('[data-partymenu-remove]');
      if (removeBtn) {
        e.preventDefault();
        this.handleRemoveProduct(removeBtn.dataset.productId);
      }
    });

    // Quantity input changes
    document.addEventListener('input', (e) => {
      const input = e.target.closest('[data-partymenu-input]');
      if (input) {
        this.handleQuantityChange(input);
      }
    });

    // Eaters count change
    const eatersInput = document.querySelector('[data-partymenu-eaters]');
    if (eatersInput) {
      eatersInput.addEventListener('change', (e) => {
        this.updateEatersCount(parseInt(e.target.value) || 10);
      });
    }
  }

  handleAddProduct(button) {
    // First try to find the partymenu-item container
    let container = button.closest('.partymenu-item');
    
    // Fallback to data-product-id if partymenu-item not found
    if (!container) {
      container = button.closest('[data-product-id]');
    }
    
    if (!container) {
      console.error('Product container not found', button);
      return;
    }
    
    // Try to find input in the container
    let input = container.querySelector('[data-partymenu-input]');
    
    // If not found in container, try finding sibling input
    if (!input) {
      const controls = button.closest('.partymenu-controls');
      if (controls) {
        input = controls.querySelector('[data-partymenu-input]');
      }
    }
    
    if (!input) {
      console.error('Quantity input not found in container', container, button);
      return;
    }
    
    const eaterCount = parseInt(input.value) || 0;
    if (eaterCount <= 0) return;

    // Get product data from JSON script tag
    const dataScript = container.querySelector('[data-partymenu-product-data]');
    if (!dataScript) {
      console.error('Product data script not found', container);
      return;
    }
    
    const productData = JSON.parse(dataScript.textContent);
    const productHandle = productData.handle;
    
    // Show loading state
    button.classList.add('btn--loading');

    // Fetch full product data to get all variants
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        // Use the smart variant selection logic from old juhlamenu.js
        const oneServingWeight = productData.serving_size;
        const variants = this.selectOptimalVariants(product, eaterCount, oneServingWeight);
        
        // Store the item with variant breakdown
        const item = {
          id: productData.id,
          handle: productData.handle,
          title: productData.title,
          image_url: productData.image_url,
          url: productData.url,
          is_cold_chain: productData.is_cold_chain,
          serving_size: oneServingWeight,
          variants: variants,
          quantity: eaterCount,
          total_weight: variants.reduce((sum, v) => sum + (v.weight * v.quantity / 1000), 0),
          total_price: variants.reduce((sum, v) => sum + v.price, 0)
        };

        this.items.set(productData.id.toString(), item);
        this.saveToLocalStorage();
        this.render();
        this.scrollToSummary();
        
        button.classList.remove('btn--loading');
        this.showNotification(`${productData.title} lisätty!`);
      })
      .catch(error => {
        console.error('Failed to fetch product data', error);
        button.classList.remove('btn--loading');
      });
  }

  /**
   * Smart variant selection - ported from old juhlamenu.js
   * Selects optimal combination of large and small variants to minimize waste
   */
  selectOptimalVariants(product, eaterCount, oneServingWeight) {
    const variants = product.variants;
    let smallestWeight = Infinity;
    let largestWeight = 0;
    let smallestVariant = null;
    let largestVariant = null;

    // Find smallest and largest variants
    variants.forEach(variant => {
      const variantOption = variant.title.split(' ');
      let weight = parseFloat(variantOption[0].replace(',', '.'));
      const unit = variantOption[1];

      if (unit === 'kg') {
        weight = weight * 1000;
      }

      if (weight > largestWeight) {
        largestWeight = weight;
        largestVariant = variant;
      }
      if (weight < smallestWeight) {
        smallestWeight = weight;
        smallestVariant = variant;
      }
    });

    let largeVariantsToAdd = 0;
    let smallVariantsToAdd = 0;
    let eatersLeftToFulfill = 0;

    // Calculate large variants needed
    if (largestWeight > 0) {
      const servingsPerLarge = largestWeight / oneServingWeight;
      
      if (eaterCount / servingsPerLarge >= 1) {
        if (eaterCount >= 30) {
          largeVariantsToAdd = Math.floor(eaterCount / servingsPerLarge);
        } else {
          largeVariantsToAdd = Math.floor(eaterCount / servingsPerLarge);
        }
        
        eatersLeftToFulfill = eaterCount - (largestWeight * largeVariantsToAdd) / oneServingWeight;
        
        // Check if we can fulfill with only large variants
        if (eatersLeftToFulfill === 0 || Number.isInteger(eaterCount / servingsPerLarge)) {
          eatersLeftToFulfill = 0;
        }
      } else {
        eatersLeftToFulfill = eaterCount;
      }
    } else {
      eatersLeftToFulfill = eaterCount;
    }

    // Calculate small variants needed for remaining servings
    if (eatersLeftToFulfill > 0) {
      const servingsPerSmall = smallestWeight / oneServingWeight;
      smallVariantsToAdd = Math.ceil(eatersLeftToFulfill / servingsPerSmall);
    }

    // Build variant array
    const selectedVariants = [];

    if (largeVariantsToAdd > 0 && largestVariant) {
      const unitPrice = largestVariant.price / 100;
      const totalPrice = unitPrice * largeVariantsToAdd;
      
      selectedVariants.push({
        id: largestVariant.id,
        title: largestVariant.title,
        weight: largestWeight,
        unit_price: unitPrice,
        price: totalPrice,
        quantity: largeVariantsToAdd,
        servings: Math.round(largeVariantsToAdd * (largestWeight / oneServingWeight))
      });
    }

    if (smallVariantsToAdd > 0 && smallestVariant) {
      const unitPrice = smallestVariant.price / 100;
      const totalPrice = unitPrice * smallVariantsToAdd;
      
      selectedVariants.push({
        id: smallestVariant.id,
        title: smallestVariant.title,
        weight: smallestWeight,
        unit_price: unitPrice,
        price: totalPrice,
        quantity: smallVariantsToAdd,
        servings: Math.round(smallVariantsToAdd * (smallestWeight / oneServingWeight))
      });
    }

    // Fallback if nothing selected
    if (selectedVariants.length === 0 && smallestVariant) {
      const unitPrice = smallestVariant.price / 100;
      
      selectedVariants.push({
        id: smallestVariant.id,
        title: smallestVariant.title,
        weight: smallestWeight,
        unit_price: unitPrice,
        price: unitPrice,
        quantity: 1,
        servings: Math.round(smallestWeight / oneServingWeight)
      });
    }

    return selectedVariants;
  }

  handleRemoveProduct(productId) {
    this.items.delete(productId);
    this.saveToLocalStorage();
    this.render();
  }

  handleQuantityChange(input) {
    // Update global eaters count for all products
    const value = parseInt(input.value) || 10;
    const allInputs = document.querySelectorAll('[data-partymenu-input]');
    allInputs.forEach(inp => {
      if (inp !== input) {
        inp.value = value;
      }
    });
  }

  updateEatersCount(count) {
    this.eaters = count;
    const allInputs = document.querySelectorAll('[data-partymenu-input]');
    allInputs.forEach(input => {
      input.value = count;
    });
  }

  calculateTotals() {
    let totalWeight = 0;
    let totalPrice = 0;
    let totalItems = 0;

    this.items.forEach(item => {
      totalWeight += item.total_weight;
      totalPrice += item.total_price;
      totalItems += item.quantity;
    });

    return {
      weight: totalWeight,
      price: totalPrice,
      items: totalItems,
      count: this.items.size
    };
  }

  render() {
    const container = document.querySelector('#jm-selected-items');
    if (!container) return;

    const totals = this.calculateTotals();

    // Render selected items with variant breakdown
    container.innerHTML = Array.from(this.items.values()).map(item => {
      const variantsHTML = item.variants.map(variant => `
        <div class="jm-variant-line">
          <span class="jm-variant-qty">${variant.quantity} × ${variant.title}</span>
          <span class="jm-variant-servings">${variant.servings} hlö</span>
          <span class="jm-variant-price">${this.formatPrice(variant.price)}</span>
        </div>
      `).join('');

      return `
        <div class="jm-selected-item" data-product-id="${item.id}">
          <div class="jm-item-header">
            <div class="jm-item-image">
              <img src="${item.image_url}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="60" height="60">
            </div>
            <div class="jm-item-summary">
              <h4 class="jm-item-title">${this.escapeHtml(item.title)}</h4>
              <div class="jm-item-totals">
                <span class="jm-item-servings">${item.quantity} hlö</span>
                <span class="jm-item-weight">${this.formatWeight(item.total_weight)}</span>
                <span class="jm-item-price">${this.formatPrice(item.total_price)}</span>
              </div>
            </div>
            <button 
              type="button" 
              class="jm-item-remove" 
              data-partymenu-remove
              data-product-id="${item.id}"
              aria-label="Poista ${this.escapeHtml(item.title)}"
            >
              ×
            </button>
          </div>
          <div class="jm-item-variants">
            ${variantsHTML}
          </div>
        </div>
      `;
    }).join('');

    // Update totals
    this.updateTotalsDisplay(totals);

    // Update floating cart
    this.updateFloatingCart(totals);

    // Toggle visibility
    const footer = document.querySelector('#jm-footer');
    const floatingCart = document.querySelector('#partymenu-floating-total');
    
    if (footer) {
      footer.style.display = totals.count > 0 ? 'block' : 'none';
    }
    if (floatingCart) {
      floatingCart.classList.toggle('visible', totals.count > 0);
    }
  }

  updateTotalsDisplay(totals) {
    const weightEl = document.querySelector('#jmc-total-weight');
    const priceEl = document.querySelector('#jmc-total');
    const countEl = document.querySelector('#selected-product-count');

    if (weightEl) weightEl.textContent = this.formatWeight(totals.weight);
    if (priceEl) priceEl.textContent = this.formatPrice(totals.price);
    if (countEl) countEl.textContent = `(${totals.count} tuotetta)`;
  }

  updateFloatingCart(totals) {
    const weightEl = document.querySelector('#jmc-floating-total-weight');
    const priceEl = document.querySelector('#jmc-floating-total-price');

    if (weightEl) weightEl.textContent = this.formatWeight(totals.weight);
    if (priceEl) priceEl.textContent = this.formatPrice(totals.price);
  }

  async addToCart() {
    if (this.items.size === 0) {
      this.showNotification('Valitse ensin tuotteita', 'error');
      return;
    }

    const button = document.querySelector('#jm-add-to-cart');
    if (button) {
      button.disabled = true;
      button.textContent = 'Lisätään...';
    }

    try {
      // Build cart items array from variant breakdown
      const cartItems = [];
      
      this.items.forEach(item => {
        item.variants.forEach(variant => {
          cartItems.push({
            id: variant.id,
            quantity: variant.quantity,
            properties: {
              'Annoksia': variant.servings,
              '_partymenu': 'true',
              '_Group': item.handle
            }
          });
        });
      });

      // Add to cart via Shopify Ajax API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) throw new Error('Cart add failed');

      // Clear selections
      this.items.clear();
      this.saveToLocalStorage();
      this.render();

      // Redirect to cart
      window.location.href = '/cart';

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Virhe lisättäessä ostoskoriin', 'error');
      
      if (button) {
        button.disabled = false;
        button.textContent = 'Jatka kassalle';
      }
    }
  }

  downloadPDF() {
    const content = document.querySelector('#jm-footer');
    if (!content) return;

    // Use browser's native print functionality
    const printWindow = window.open('', '_blank');
    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Partymenu - ${new Date().toLocaleDateString('fi-FI')}</title>
          <style>${styles}</style>
          <style>
            @media print {
              body { padding: 20px; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }

  scrollToSummary() {
    const anchor = document.querySelector('#jm-anchor');
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  showNotification(message, type = 'success') {
    // Create or reuse notification element
    let notification = document.querySelector('.partymenu-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'partymenu-notification';
      document.body.appendChild(notification);
    }

    notification.textContent = message;
    notification.className = `partymenu-notification partymenu-notification--${type} active`;

    setTimeout(() => {
      notification.classList.remove('active');
    }, 3000);
  }

  saveToLocalStorage() {
    try {
      const data = Array.from(this.items.entries());
      localStorage.setItem('partymenu_items', JSON.stringify(data));
    } catch (e) {
      console.warn('Could not save to localStorage:', e);
    }
  }

  restoreFromLocalStorage() {
    try {
      const data = localStorage.getItem('partymenu_items');
      if (data) {
        const items = JSON.parse(data);
        this.items = new Map(items);
        this.render();
      }
    } catch (e) {
      console.warn('Could not restore from localStorage:', e);
    }
  }

  formatPrice(euros) {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR'
    }).format(euros);
  }

  formatWeight(kg) {
    return `${kg.toFixed(2)} kg`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register the web component
customElements.define('partymenu-calculator', PartymenuCalculator);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPartymenu);
} else {
  initPartymenu();
}

function initPartymenu() {
  const calculator = document.querySelector('partymenu-calculator');
  if (!calculator) {
    // Create calculator element if it doesn't exist
    const calc = document.createElement('partymenu-calculator');
    document.body.appendChild(calc);
  }

  // Attach event handlers
  const addToCartBtn = document.querySelector('#jm-add-to-cart');
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', () => {
      const calc = document.querySelector('partymenu-calculator');
      if (calc) calc.addToCart();
    });
  }

  const downloadPdfBtn = document.querySelector('#jm-download-pdf');
  if (downloadPdfBtn) {
    downloadPdfBtn.addEventListener('click', () => {
      const calc = document.querySelector('partymenu-calculator');
      if (calc) calc.downloadPDF();
    });
  }

  // Collapsible sections
  document.querySelectorAll('[data-partymenu-toggle]').forEach(toggle => {
    toggle.addEventListener('click', function() {
      const target = this.getAttribute('aria-controls');
      const content = document.getElementById(target);
      if (content) {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !isExpanded);
        content.classList.toggle('active');
      }
    });
  });
}
